import { cn } from '../../lib/cn';
import { formatMultiplier } from '../../lib/format';
import type { MarketHit } from '../../lib/round';

export function ConfidenceBadge({
  weight,
  active = false,
  dark = false
}: {
  weight: number;
  active?: boolean;
  /** Render for a dark surface (the live "Watch live" board). */
  dark?: boolean;
}) {
  const bg = dark
    ? active
      ? 'bg-accent'
      : 'bg-white/10'
    : active
      ? 'bg-night'
      : 'bg-fill2';
  const symbol = dark ? (active ? 'text-[#06371F]' : 'text-white/50') : active ? 'text-white' : 'text-ink3';
  const value = dark ? (active ? 'text-[#06371F]' : 'text-white') : active ? 'text-white' : 'text-ink';
  return (
    <div className={cn('flex items-baseline gap-[1px] rounded-lg px-[7px] py-1', bg)}>
      <span className={cn('text-[10px] font-bold', symbol)}>×</span>
      <span className={cn('num text-[13px] font-extrabold leading-none', value)}>{formatMultiplier(weight)}</span>
    </div>
  );
}

/** The Build-Your-Card analog of the confidence badge: the card's combined boost
 *  multiplier (×N). Active = the card is live/banking. Float-friendly (e.g. ×4.3). */
export function MultiplierBadge({
  multiplier,
  active = false,
  dark = false
}: {
  multiplier: number;
  active?: boolean;
  dark?: boolean;
}) {
  const bg = dark ? (active ? 'bg-accent' : 'bg-white/10') : active ? 'bg-night' : 'bg-fill2';
  const symbol = dark ? (active ? 'text-[#06371F]' : 'text-white/50') : active ? 'text-white' : 'text-ink3';
  const value = dark ? (active ? 'text-[#06371F]' : 'text-white') : active ? 'text-white' : 'text-ink';
  return (
    <div className={cn('flex items-baseline gap-[1px] rounded-lg px-[7px] py-1', bg)}>
      <span className={cn('text-[10px] font-bold', symbol)}>×</span>
      <span className={cn('num text-[13px] font-extrabold leading-none', value)}>{formatMultiplier(multiplier)}</span>
    </div>
  );
}

/** Dot styling per market state — a certainty gradient from open → provisional
 *  (on track / trailing, while live) → settled (hit / miss). */
const DOT_TONE: Record<MarketHit, string> = {
  hit: 'bg-accent',
  miss: 'bg-miss',
  ontrack: 'border-[1.5px] border-accent bg-accent/30',
  offtrack: 'border-[1.5px] border-miss bg-miss/20',
  open: 'border-[1.5px] border-ink4 bg-transparent'
};

// The only tone that needs to flip for a dark surface is the neutral "open" dot —
// its light-grey ring is invisible on near-black, so it gets a translucent white ring.
const DOT_TONE_DARK: Record<MarketHit, string> = {
  ...DOT_TONE,
  open: 'border-[1.5px] border-white/30 bg-transparent'
};

export function MarketDots({ markets, dark = false }: { markets: MarketHit[]; dark?: boolean }) {
  const tones = dark ? DOT_TONE_DARK : DOT_TONE;
  return (
    <div className="flex items-center gap-[5px]">
      {markets.map((m, i) => (
        <span key={i} className={cn('h-[9px] w-[9px] rounded-full', tones[m])} />
      ))}
    </div>
  );
}

export function PickLegend({
  variant = 'settled',
  dark = false
}: {
  variant?: 'settled' | 'live';
  dark?: boolean;
}) {
  const tones = dark ? DOT_TONE_DARK : DOT_TONE;
  const items =
    variant === 'live'
      ? ([
          { tone: tones.ontrack, label: 'On track' },
          { tone: tones.offtrack, label: 'Trailing' }
        ] as const)
      : ([
          { tone: tones.hit, label: 'Hit' },
          { tone: tones.miss, label: 'Miss' },
          { tone: tones.open, label: 'Open' }
        ] as const);

  return (
    <div className="flex items-center gap-[11px]">
      {items.map(({ tone, label }) => (
        <div key={label} className="flex items-center gap-1">
          <span className={cn('h-[7px] w-[7px] rounded-full', tone)} />
          <span className={cn('text-[10px] font-semibold', dark ? 'text-white/55' : 'text-ink2')}>{label}</span>
        </div>
      ))}
    </div>
  );
}

export function LivePill() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-accent-wash px-[7px] py-[2px]">
      <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
      <span className="text-[10px] font-extrabold uppercase tracking-wide text-accent-ink">Live</span>
    </span>
  );
}
